import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Mail,
  Facebook,
  Youtube,
  Instagram,
  MessageCircle,
  X,
  Radio,
  Church,
  ExternalLink,
} from "lucide-react";
import {
  LOGO_LIFETALK_RADIO,
  LINK_RADIO_FB,
  LINK_CHURCH_FB,
  LINK_CHURCH_YT,
  LINK_CHURCH_IG,
  CONTACT_PHONES,
  EMAILS_LIFETALK,
  EMAILS_FATHERS_HOUSE,
  whatsappHrefFor,
  CHURCH_ADDRESS,
  MAP_LINK_CHURCH,
  RADIO_OFFICE_ADDRESS,
  MAP_LINK_RADIO,
} from "../../lib/siteConfig";

export default function Footer() {
  const [fbPicker, setFbPicker] = useState(false);
  const [waPicker, setWaPicker] = useState(false);

  const social = [
    {
      key: "fb",
      label: "Facebook",
      Icon: Facebook,
      onClick: () => setFbPicker(true),
    },
    { key: "yt", label: "YouTube", Icon: Youtube, href: LINK_CHURCH_YT },
    { key: "ig", label: "Instagram", Icon: Instagram, href: LINK_CHURCH_IG },
    {
      key: "wa",
      label: "WhatsApp",
      Icon: MessageCircle,
      onClick: () => setWaPicker(true),
    },
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-[#1F2833] bg-[#0B0C10]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-12 h-12 bg-white overflow-hidden ring-1 ring-[#C5D1EB]/20">
              <img
                src={LOGO_LIFETALK_RADIO}
                alt="Life Talk Trust"
                className="w-12 h-12 object-contain"
              />
            </span>
            <div>
              <p className="font-display text-white font-semibold tracking-tight">
                Life Talk Trust
              </p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70">
                Bringing life into your world
              </p>
            </div>
          </div>
          <p className="mt-5 text-sm text-[#C5D1EB]/85 max-w-md leading-relaxed">
            A multi-ethnic gospel radio ministry across New Zealand, paired with
            Father's House Indian Church Tauranga. Led by Pastors Robinson & Esther Martin.
          </p>

          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            {social.map(({ key, label, Icon, href, onClick }) => {
              const cls =
                "inline-flex items-center justify-center w-9 h-9 border border-[#C5D1EB]/30 text-[#C5D1EB] hover:text-white hover:border-[#C5001A] hover:bg-[#1F2833] transition-colors";
              return onClick ? (
                <button
                  key={key}
                  type="button"
                  onClick={onClick}
                  aria-label={label}
                  data-testid={`footer-social-${key}`}
                  className={cls}
                >
                  <Icon size={15} />
                </button>
              ) : (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${key}`}
                  className={cls}
                >
                  <Icon size={15} />
                </a>
              );
            })}
          </div>
        </div>

        {/* Two operational addresses */}
        <div className="md:col-span-5 grid sm:grid-cols-2 gap-5">
          <FooterAddress
            Icon={Radio}
            label="Life Talk Radio Office"
            address={RADIO_OFFICE_ADDRESS}
            mapLink={MAP_LINK_RADIO}
            testid="footer-address-radio"
          />
          <FooterAddress
            Icon={Church}
            label="Father's House Church"
            address={CHURCH_ADDRESS}
            mapLink={MAP_LINK_CHURCH}
            testid="footer-address-church"
          />
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70 mb-4">
            Contact
          </p>

          <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/55 mb-2">
            WhatsApp & Phone
          </p>
          <ul className="space-y-2 text-sm">
            {CONTACT_PHONES.map((p) => (
              <li key={p}>
                <a
                  href={whatsappHrefFor(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-phone-${p}`}
                  className="flex items-center gap-2 text-white/90 hover:text-white"
                >
                  <MessageCircle size={14} className="text-[#C5001A]" />
                  {p}
                </a>
              </li>
            ))}
          </ul>

          <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/55 mt-5 mb-2">
            Life Talk Trust
          </p>
          <ul className="space-y-1.5 text-sm">
            {EMAILS_LIFETALK.map((em) => (
              <li key={em}>
                <a
                  href={`mailto:${em}`}
                  data-testid={`footer-email-lt-${em}`}
                  className="flex items-center gap-2 text-white/90 hover:text-white break-all"
                >
                  <Mail size={13} className="text-[#C5001A] shrink-0" />
                  {em}
                </a>
              </li>
            ))}
          </ul>

          <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/55 mt-5 mb-2">
            Father's House Church
          </p>
          <ul className="space-y-1.5 text-sm">
            {EMAILS_FATHERS_HOUSE.map((em) => (
              <li key={em}>
                <a
                  href={`mailto:${em}`}
                  data-testid={`footer-email-fh-${em}`}
                  className="flex items-center gap-2 text-white/90 hover:text-white break-all"
                >
                  <Mail size={13} className="text-[#C5001A] shrink-0" />
                  {em}
                </a>
              </li>
            ))}
          </ul>

          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70 mt-7 mb-3">
            Explore
          </p>
          <ul className="space-y-2 text-sm">
            {[
              ["/listen", "Radio Ministry"],
              ["/church", "Father's House Church"],
              ["/giving", "Giving"],
              ["/testimonies", "Testimonies"],
              ["/contact", "Contact Us"],
            ].map(([to, l]) => (
              <li key={to}>
                <Link
                  to={to}
                  className="text-[#C5D1EB]/80 hover:text-white transition-colors"
                >
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-[#1F2833]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#C5D1EB]/70">
          <p>© {new Date().getFullYear()} Life Talk Trust · Father's House Indian Church Tauranga.</p>
          <p data-testid="footer-compliance">
            Broadcasting under APRA / AMCOS compliance. © Life Talk Trust. All Rights Reserved.
          </p>
        </div>
      </div>

      {fbPicker && (        <div
          data-testid="footer-fb-picker"
          className="fixed inset-0 z-[60] flex items-center justify-center px-5"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-[#0B0C10]/80 backdrop-blur-sm"
            onClick={() => setFbPicker(false)}
          />
          <div className="relative bg-[#1F2833] border border-white/10 max-w-md w-full p-7 sm:p-8">
            <button
              data-testid="footer-fb-picker-close"
              onClick={() => setFbPicker(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#C5D1EB] hover:text-white"
            >
              <X size={16} />
            </button>
            <Facebook size={22} className="text-[#1877F2]" />
            <h3 className="mt-3 font-display text-xl text-white tracking-tight">
              Choose a Facebook page
            </h3>
            <p className="mt-2 text-sm text-[#C5D1EB] leading-relaxed">
              We run two Facebook pages — which would you like to visit?
            </p>
            <div className="mt-6 grid gap-3">
              <a
                href={LINK_RADIO_FB}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setFbPicker(false)}
                className="flex items-center gap-3 bg-[#0B0C10] border border-white/10 hover:border-[#C5001A] p-4 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center bg-[#1877F2]/15 text-[#1877F2]">
                  <Radio size={17} />
                </span>
                <div className="flex-1 text-left">
                  <p className="font-display text-white text-sm">Life Talk Radio</p>
                  <p className="text-[11px] text-[#C5D1EB]/70">@lifetalktga</p>
                </div>
              </a>
              <a
                href={LINK_CHURCH_FB}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setFbPicker(false)}
                className="flex items-center gap-3 bg-[#0B0C10] border border-white/10 hover:border-[#C5001A] p-4 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center bg-[#1877F2]/15 text-[#1877F2]">
                  <Church size={17} />
                </span>
                <div className="flex-1 text-left">
                  <p className="font-display text-white text-sm">
                    Father's House Tauranga
                  </p>
                  <p className="text-[11px] text-[#C5D1EB]/70">
                    Indian Church · NZ
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp number chooser modal */}
      {waPicker && (
        <div
          data-testid="footer-wa-picker"
          className="fixed inset-0 z-[60] flex items-center justify-center px-5"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-[#0B0C10]/80 backdrop-blur-sm"
            onClick={() => setWaPicker(false)}
          />
          <div className="relative bg-[#1F2833] border border-white/10 max-w-md w-full p-7 sm:p-8 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)]">
            <button
              data-testid="footer-wa-picker-close"
              onClick={() => setWaPicker(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#C5D1EB] hover:text-white"
            >
              <X size={16} />
            </button>
            <MessageCircle size={22} className="text-[#25D366]" />
            <h3 className="mt-3 font-display text-xl text-white tracking-tight">
              Choose a WhatsApp number
            </h3>
            <p className="mt-2 text-sm text-[#C5D1EB] leading-relaxed">
              Tap a number to open the chat directly.
            </p>
            <div className="mt-6 grid gap-3">
              {CONTACT_PHONES.map((p, i) => (
                <a
                  key={p}
                  href={whatsappHrefFor(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-wa-pick-${i}`}
                  onClick={() => setWaPicker(false)}
                  className="group flex items-center gap-3 bg-[#0B0C10] border border-white/10 hover:border-[#25D366] p-4 transition-colors"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                    <MessageCircle size={17} />
                  </span>
                  <div className="flex-1 text-left">
                    <p className="text-[11px] tracking-[0.25em] uppercase text-[#C5D1EB]/70">
                      Contact {i + 1}
                    </p>
                    <p className="font-display text-white text-sm">{p}</p>
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/60 group-hover:text-white">
                    Chat →
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}


/* -------------------------------------------------------------------------- */
/*  Compact address card used inside the footer                                */
/* -------------------------------------------------------------------------- */
function FooterAddress({ Icon, label, address, mapLink, testid }) {
  return (
    <div data-testid={testid} className="text-sm">
      <p className="text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70 mb-3 flex items-center gap-2">
        <Icon size={13} className="text-[#C5001A]" />
        {label}
      </p>
      <p className="text-white/85 leading-relaxed flex items-start gap-2">
        <MapPin size={13} className="mt-0.5 text-[#C5001A] shrink-0" />
        <span>{address}</span>
      </p>
      <a
        href={mapLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testid}-link`}
        className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-[#C5D1EB]/80 hover:text-white border-b border-[#C5001A]/40 hover:border-[#C5001A] pb-0.5 transition-colors"
      >
        View on Google Maps
        <ExternalLink size={11} />
      </a>
    </div>
  );
}
